import { useEffect, useState, useRef, useCallback, useMemo } from "preact/hooks";
import type { GravityForm } from "@services/wordpress/wordpress.types";
import { Email } from "@components/Forms/GravityFormComponent/Inputs/Email";
import styles from './index.module.scss';

interface EmailCaptureModalProps {
  email_capture_form?: GravityForm;
  openEmailCaptureModal: boolean;
  setOpenEmailCaptureModal: (open: boolean) => void;
  showValidationMessage: boolean;
  setShowValidationMessage: (show: boolean) => void;
  combinedSelectedItems: {
    state: any;
    insurance: any;
    specialty: any;
  };
  getRedirectUrl?: () => string;
  handleRedirectClick?: (event: Event) => void;
}

const renderFormField = (field: any, showValidationMessage: boolean) => {
  if (field.type === 'html' && field.content) {
    return (
      <div key={field.id} dangerouslySetInnerHTML={{ __html: field.content }} />
    );
  } else if (field.type === 'email') {
    return (
      <Email
        key={field.id}
        autocompleteAttribute={field.autocompleteAttribute}
        enableAutocomplete={field.enableAutocomplete}
        id={`${field.id}`}
        label={field.label}
        placeholder={field.placeholder}
        showValidationMessage={showValidationMessage}
      />
    );
  } else if (field.type === 'hidden') {
    return (
      <input 
        key={field.id}
        id={String(field.id)} 
        type="hidden" 
        value={field.defaultValue} 
        name={`input_${field.id}`} 
      />
    );
  }
  return null;
};

export const EmailCaptureModal = ({
  email_capture_form,
  openEmailCaptureModal,
  setOpenEmailCaptureModal,
  showValidationMessage,
  setShowValidationMessage,
  combinedSelectedItems,
  getRedirectUrl,
  handleRedirectClick,
}: EmailCaptureModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalValidationMessage, setModalValidationMessage] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const currentRedirectUrl = useMemo(() => {
    return getRedirectUrl?.() || "";
  }, [getRedirectUrl]);

  const handleEmailCaptureSubmit = useCallback(async (event: Event) => {
    event.preventDefault();
    setIsSubmitting(true);
    
    const formEl = event.target as HTMLFormElement;

    // Dynamically populate hidden fields
    try {
      const locationInput = formEl.querySelector('input[value="{pre_srp_location}"]') as HTMLInputElement;
      if (locationInput && combinedSelectedItems.state?.value) {
        locationInput.value = combinedSelectedItems.state.value;
      }

      const insuranceInput = formEl.querySelector('input[value="{pre_srp_insurance}"]') as HTMLInputElement;
      if (insuranceInput && combinedSelectedItems.insurance?.value) {
        insuranceInput.value = combinedSelectedItems.insurance.value;
      }

      const needsInput = formEl.querySelector('input[value="{pre_srp_needs}"]') as HTMLInputElement;
      if (needsInput && combinedSelectedItems.specialty?.values) {
        needsInput.value = Array.from(combinedSelectedItems.specialty.values()).map((s: any) => s.value).join(', ');
      }

      const redirectUrlInput = formEl.querySelector('input[value="{pre_srp_url}"]') as HTMLInputElement;
      if (redirectUrlInput && currentRedirectUrl) {
        redirectUrlInput.value = currentRedirectUrl;
      }

      const formData = new FormData(formEl);

      const response = await fetch(`${import.meta.env.PUBLIC_WP_APP_URL}/wp-json/gf/v2/forms/${email_capture_form?.id}/submissions/`, {
        method: 'POST',
        body: formData,
      });

      const json = await response.json();

      if (json?.is_valid) {
        setModalValidationMessage(false);
        handleRedirectClick?.(event);
      } else {
        setModalValidationMessage(true);
      }
    } catch (err) {
      console.error("Form submission failed", err);
      setModalValidationMessage(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [combinedSelectedItems, currentRedirectUrl, email_capture_form?.id, setShowValidationMessage, handleRedirectClick]);

  const handleCloseModal = useCallback(() => {
    setOpenEmailCaptureModal(false);
    setModalValidationMessage(false);
    document.body.style.overflow = '';
  }, [setOpenEmailCaptureModal]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      handleCloseModal();
    }
  }, [handleCloseModal]);

  const handleEscapeKey = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleCloseModal();
    }
  }, [handleCloseModal]);

  const handleContinueClick = useCallback((event: Event) => {
    handleRedirectClick?.(event);
    handleCloseModal();
  }, [handleRedirectClick, handleCloseModal]);

  useEffect(() => {
    if (!openEmailCaptureModal) return;

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);
    
    const formEl = formRef.current;
    if (formEl) {
      formEl.addEventListener("submit", handleEmailCaptureSubmit);
    }

    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
      if (formEl) {
        formEl.removeEventListener("submit", handleEmailCaptureSubmit);
      }
      document.body.style.overflow = '';
    };
  }, [openEmailCaptureModal, handleClickOutside, handleEscapeKey, handleEmailCaptureSubmit]);

  if (!openEmailCaptureModal || !email_capture_form) {
    return null;
  }

  return (
    <div 
      className={styles.emailCaptureModal}
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-capture-modal-title"
      aria-describedby="email-capture-modal-description"
    >
      <div 
        ref={modalRef}
        className={styles.emailCaptureModalContent}
        role="document"
      >
        <form ref={formRef}>
          {email_capture_form.fields.map(field => 
            renderFormField(field, modalValidationMessage)
          )}

          {email_capture_form.button && (
            <button
              className={styles.emailCaptureModalButton}
              id={email_capture_form.button.id}
              type="submit"
              disabled={isSubmitting}
              aria-describedby={modalValidationMessage ? "validation-message" : undefined}
            >
              {isSubmitting ? "Submitting..." : email_capture_form.button.text}
            </button>
          )}
        </form>

        {modalValidationMessage && (
          <div id="validation-message" className={styles.validationMessage} role="alert">
            Please check your email address and try again.
          </div>
        )}

        <button 
          className={styles.emailCaptureModalLink} 
          type="button" 
          onClick={handleContinueClick}
          disabled={isSubmitting}
        >
          Continue to my results
        </button>
      </div>
    </div>
  );
};
